import useClickOutside from '@/hooks/useClickOutside';
import useDisableBodyScroll from '@/hooks/useDisableBodyScroll';
import { changeSorting } from '@/utils/analytics.js';
import { SideSheet, DropDownOption, Button, FiltersList, FilterSkeleton } from '@desquared/wave-components-library';
import React, { useState } from 'react';
import styles from './FiltersAside.module.css';
import { useTranslations } from 'next-intl';

export default function FiltersAside({
  isLoading,
  handleClose,
  handleApplyFilters,
  setSelectedSort,
  selectedSort,
  hasClosest,
  selectedFilters,
  setSelectedFilters,
  tags,
  brands,
  attributes,
  categoryKinds,
  clearFilters,
  hideOffers,
  hideSort,
  isOffers,
}) {
  const [selectedIndex, setSelectedIndex] = useState(selectedSort || 0);
  const ref = useClickOutside(handleClose, []);
  const t = useTranslations();

  const OFFERS_OPTIONS = [
    { id: 'withOffer', name: t('category_details.offer_options.discount'), disabled: isOffers },
    { id: 'withBundleOffers', name: t('category_details.offer_options.bundle'), disabled: isOffers },
  ];
  //ToDo: This should probably come from the DS by default
  useDisableBodyScroll();

  const sortingOptions = hasClosest
    ? [
        {
          unavailable: false,
          name: t('category_details.sorting_filters.sorting.options.most_relevant'),
        },
        { unavailable: false, name: t('category_details.sorting_filters.sorting.options.alphabetic') },
        { unavailable: false, name: t('category_details.sorting_filters.sorting.options.price_asc') },
        { unavailable: false, name: t('category_details.sorting_filters.sorting.options.price_desc') },
        { unavailable: false, name: t('category_details.sorting_filters.sorting.options.kilo_price') },
      ]
    : [
        {
          unavailable: false,
          name: t('category_details.sorting_filters.sorting.options.pre_selection'),
        },
        { unavailable: false, name: t('category_details.sorting_filters.sorting.options.alphabetic') },
        { unavailable: false, name: t('category_details.sorting_filters.sorting.options.price_asc') },
        { unavailable: false, name: t('category_details.sorting_filters.sorting.options.price_desc') },
        { unavailable: false, name: t('category_details.sorting_filters.sorting.options.kilo_price') },
      ];
  return (
    <div className={styles.wrapper}>
      <SideSheet
        header={t('category_details.sorting_filters.title')}
        hasClose={true}
        onClose={handleClose}
        classOption={styles.dynamicViewHeight}
      >
        <div ref={ref} className={styles.contentContainer}>
          <div className={styles.content}>
            {!hideSort && (
              <DropDownOption
                title={t('category_details.sorting_filters.sorting.title')}
                selectedOptionIndex={selectedIndex}
                onChangeSelectedOptionIndex={index => {
                  setSelectedIndex(index);
                  changeSorting(sortingOptions[index].name);
                }}
                options={sortingOptions}
              />
            )}

            {isLoading ? (
              <div className={styles.skeletonContainer}>
                <FilterSkeleton />
                <FilterSkeleton />
                <FilterSkeleton />
                <FilterSkeleton />
                <FilterSkeleton />
              </div>
            ) : (
              <>
                {!hideOffers ? (
                  <FiltersList
                    id={'offers'}
                    title={t('category_details.offer_options.title')}
                    options={OFFERS_OPTIONS}
                    selected={selectedFilters?.offers || []}
                    onChange={item => {
                      //when we are in offers we should not allow for disabling any offer options
                      if (isOffers) {
                        return;
                      }
                      changeSorting(item);
                      setSelectedFilters('offers', item);
                    }}
                  />
                ) : null}
                {tags?.length > 0 ? (
                  <FiltersList
                    id={'tags'}
                    title={t('category_details.sorting_filters.filters.tags.title')}
                    options={tags}
                    selected={selectedFilters?.tags || []}
                    onChange={item => {
                      changeSorting(item);
                      setSelectedFilters('tags', item);
                    }}
                    showLessLabel={t('category_details.sorting_filters.show_less')}
                    showMoreLabel={t('category_details.sorting_filters.show_more')}
                  />
                ) : null}
                {categoryKinds?.length > 0 ? (
                  <FiltersList
                    id={'categoryKinds'}
                    title={t('category_details.sorting_filters.filters.kind.title')}
                    options={categoryKinds}
                    selected={selectedFilters?.categoryKinds || []}
                    onChange={item => {
                      changeSorting(item);
                      setSelectedFilters('categoryKinds', item);
                    }}
                    showLessLabel={t('category_details.sorting_filters.show_less')}
                    showMoreLabel={t('category_details.sorting_filters.show_more')}
                  />
                ) : null}
                {attributes?.length > 0 ? (
                  <FiltersList
                    id={'attributes'}
                    title={t('category_details.sorting_filters.filters.attributes.title')}
                    options={attributes}
                    selected={selectedFilters?.attributes || []}
                    onChange={item => {
                      changeSorting(item);
                      setSelectedFilters('attributes', item);
                    }}
                    showLessLabel={t('category_details.sorting_filters.show_less')}
                    showMoreLabel={t('category_details.sorting_filters.show_more')}
                  />
                ) : null}
                {brands?.length > 0 ? (
                  <FiltersList
                    id={'brands'}
                    title={t('category_details.sorting_filters.filters.brands.title')}
                    options={brands}
                    selected={selectedFilters?.brands || []}
                    onChange={item => {
                      changeSorting(item);
                      setSelectedFilters('brands', item);
                    }}
                    showLessLabel={t('category_details.sorting_filters.show_less')}
                    showMoreLabel={t('category_details.sorting_filters.show_more')}
                  />
                ) : null}
              </>
            )}
          </div>
          <div className={styles.footer}>
            <Button
              onClick={() => {
                setSelectedIndex(0);
                clearFilters(!!isOffers);
                handleClose();
              }}
              variant={'text'}
              text={t('category_details.sorting_filters.clear_btn')}
            />
            <Button
              onClick={() => {
                setSelectedSort(selectedIndex);
                handleApplyFilters();
                handleClose();
              }}
              text={t('category_details.sorting_filters.apply_btn')}
            />
          </div>
        </div>
      </SideSheet>
    </div>
  );
}
